// TypeScript type definitions for Time Tracking system
// NOTE: DateTime utility functions have been moved to services/dateTimeService.ts
// Import from there instead of using the functions below (kept for backward compatibility)

export interface BreakRecord {
  id: number;
  timeEntryId: number;
  breakStart: string; // ISO datetime string
  breakEnd?: string; // ISO datetime string
  breakDurationMinutes: number;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TimeEntry {
  id: number;
  employeeId: number;
  employeeName: string;
  workDate: string; // ISO date string (YYYY-MM-DD)
  timeIn: string; // ISO datetime string
  timeOut?: string; // ISO datetime string
  totalHours: number;
  status: TimeEntryStatus;
  notes?: string;
  adminEditNote?: string; // Admin note for timesheet edits
  createdAt: string;
  updatedAt: string;
  isWorkdayCompleted?: boolean; // Whether workday completion criteria was met
  hasHalfDayLeave?: boolean; // Whether employee has approved half-day leave
  // Break tracking fields
  isOnBreak?: boolean;
  totalBreakMinutes?: number;
  breakRecords?: BreakRecord[];
}

export enum TimeEntryStatus {
  IN = "IN",
  OUT = "OUT",
}

export interface EmployeeStatus {
  employeeId: number;
  employeeName: string;
  department?: string;
  currentStatus: TimeEntryStatus | null;
  lastTimeIn?: string;
  lastTimeOut?: string;
  lastActivity?: string;
  currentSessionDuration: string;
  totalHoursWorked?: number;
  isWorkdayCompleted?: boolean;
  hasHalfDayLeave?: boolean;
  // Break tracking fields
  isOnBreak?: boolean;
  totalBreakMinutes?: number;
  currentBreakStart?: string;
}

export interface TimesheetReport {
  employeeId: number;
  employeeName: string;
  department?: string;
  startDate: string;
  endDate: string;
  timeEntries: TimeEntry[];
  totalHours: number;
  totalDays: number;
  averageHoursPerDay: number;
  reportGeneratedAt: string;
}

export interface TimeInRequest {
  employeeId: number;
  employeeName?: string;
  notes?: string;
  timezone?: string; // Timezone ID (e.g., 'Asia/Tokyo')
}

export interface TimeOutRequest {
  employeeId: number;
  notes?: string;
  timezone?: string; // Timezone ID (e.g., 'Asia/Tokyo')
}

export interface BreakStartRequest {
  employeeId: number;
  notes?: string;
  timezone?: string; // Timezone ID (e.g., 'Asia/Tokyo')
}

export interface BreakEndRequest {
  employeeId: number;
  timezone?: string; // Timezone ID (e.g., 'Asia/Tokyo')
}

export interface TimeTrackingDashboard {
  currentStatus: EmployeeStatus;
  todayEntries: TimeEntry[];
  weekSummary: {
    totalHours: number;
    totalDays: number;
    averageHours: number;
  };
  monthSummary: {
    totalHours: number;
    totalDays: number;
    averageHours: number;
  };
  recentActivity: TimeEntry[];
}

export interface AdminDashboard {
  totalEmployees: number;
  currentlyWorking: number;
  totalHoursToday: number;
  averageHoursPerEmployee: number;
  topWorkers: {
    employeeName: string;
    totalHours: number;
  }[];
  departmentSummary: {
    department: string;
    employeeCount: number;
    totalHours: number;
    averageHours: number;
  }[];
}

// Utility functions
export const getStatusColor = (
  status: TimeEntryStatus | null,
  isWorkdayCompleted?: boolean,
  isOnBreak?: boolean,
): "success" | "warning" | "error" | "default" | "primary" | "info" => {
  // If employee is on break, show info (blue)
  if (isOnBreak) {
    return "info"; // Blue - On break
  }

  // If employee has timed out but workday is not completed, show error (red)
  if (status === TimeEntryStatus.OUT && !isWorkdayCompleted) {
    return "error"; // Red - Work hours incomplete
  }

  switch (status) {
    case TimeEntryStatus.IN:
      return "success"; // Green - Currently working
    case TimeEntryStatus.OUT:
      return "primary"; // Blue - Workday completed
    default:
      return "default"; // Grey - Not yet in
  }
};

export const getStatusText = (
  status: TimeEntryStatus | null,
  isWorkdayCompleted?: boolean,
  isOnBreak?: boolean,
): string => {
  // If employee is on break
  if (isOnBreak) {
    return "timetracking.status.ON_BREAK";
  }

  // If employee has timed out but workday is not completed
  if (status === TimeEntryStatus.OUT && !isWorkdayCompleted) {
    return "timetracking.status.WORK_HOURS_INCOMPLETE";
  }

  switch (status) {
    case TimeEntryStatus.IN:
      return "timetracking.status.IN";
    case TimeEntryStatus.OUT:
      return "timetracking.status.WORKDAY_COMPLETED";
    default:
      return "timetracking.status.NOT_YET_IN";
  }
};

export const formatDuration = (hours: number): string => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  return `${wholeHours}h ${minutes}m`;
};

// UTC/Local Time Conversion Utilities
// @deprecated These functions are kept for backward compatibility.
// New code should use services/dateTimeService.ts instead.

/**
 * Convert UTC date/time to local timezone for display
 * @deprecated Use dateTimeService.utcToLocal() instead
 */
export const utcToLocal = (utcDateTime: string | any): Date => {
  if (!utcDateTime) {
    throw new Error("UTC date time is required");
  }

  let date: Date;

  const toLocalDateTime = (arr: number[]): Date => {
    if (!Array.isArray(arr) || arr.length < 6) return new Date();

    const [year, month, day, hour, minute, second] = arr;

    // Create Date in UTC based on backend values
    const utcDate = new Date(
      Date.UTC(year, month - 1, day, hour, minute, second),
    );

    // JavaScript Date automatically represents it in the browser’s local time zone
    return utcDate;
  };

  if (typeof utcDateTime === "number") {
    // Handle Unix timestamp (in seconds with possible decimals)
    // Backend sends timestamps like 1761553765.659089 (seconds with decimals)
    // Convert to milliseconds by multiplying by 1000
    date = new Date(utcDateTime * 1000);
  } else if (typeof utcDateTime === "string") {
    // Try to parse as ISO 8601 string first
    if (utcDateTime.includes("T")) {
      date = new Date(utcDateTime);
    } else {
      // Try to parse as Unix timestamp string
      const timestamp = parseFloat(utcDateTime);
      if (!isNaN(timestamp)) {
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(utcDateTime);
      }
    }
  } else if (Array.isArray(utcDateTime)) {
    // Handle array format [year, month, day, hour, minute, second]
    date = toLocalDateTime(utcDateTime);
  } else if (typeof utcDateTime === "object" && utcDateTime !== null) {
    // Handle object format
    const { year, month, day, hour = 0, minute = 0, second = 0 } = utcDateTime;
    date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  } else {
    date = new Date(utcDateTime);
  }

  if (isNaN(date.getTime())) {
    throw new Error("Invalid UTC date format");
  }

  return date;
};

/**
 * Convert local date/time to UTC for sending to backend
 */
export const localToUtc = (localDateTime: Date): string => {
  return localDateTime.toISOString();
};

/**
 * Get current UTC date/time as ISO string
 */
export const nowUtc = (): string => {
  return new Date().toISOString();
};

/**
 * Get today's date in UTC
 */
export const todayUtc = (): string => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  )
    .toISOString()
    .split("T")[0];
};

export const formatTime = (dateTimeString: string | any): string => {
  if (!dateTimeString) {
    return "N/A";
  }

  try {
    // Convert UTC to local time for display
    const localDate = utcToLocal(dateTimeString);

    return localDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.warn("Invalid date format received:", dateTimeString, error);
    return "Invalid Date";
  }
};

export const formatDate = (dateString: string | any): string => {
  if (!dateString) {
    return "N/A";
  }

  try {
    let date: Date;

    if (Array.isArray(dateString)) {
      // Handle UTC array format - convert from UTC to local
      const localDate = utcToLocal(dateString);
      date = localDate;
    } else if (typeof dateString === "string") {
      // Handle ISO string - convert from UTC to local
      const localDate = utcToLocal(dateString);
      date = localDate;
    } else {
      // Fallback to UTC conversion
      const localDate = utcToLocal(dateString);
      date = localDate;
    }

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    console.warn("Invalid date format received:", dateString, error);
    return "Invalid Date";
  }
};

export const formatDateTime = (dateTimeString: string | any): string => {
  if (!dateTimeString) {
    return "N/A";
  }

  try {
    // Convert UTC to local time for display
    const localDate = utcToLocal(dateTimeString);

    return localDate.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.warn("Invalid date format received:", dateTimeString, error);
    return "Invalid Date";
  }
};

export const calculateSessionDuration = (
  timeIn: string | any,
  timeOut?: string | any,
): string => {
  if (!timeIn) {
    return "0h 0m";
  }

  try {
    // Parse UTC times and convert to local for calculation
    const startTime = utcToLocal(timeIn);
    const endTime = timeOut ? utcToLocal(timeOut) : new Date(); // Current local time for ongoing sessions

    const durationMs = endTime.getTime() - startTime.getTime();
    const hours = Math.max(0, durationMs / (1000 * 60 * 60)); // Ensure non-negative
    return formatDuration(hours);
  } catch (error) {
    console.warn(
      "Invalid date format in calculateSessionDuration:",
      { timeIn, timeOut },
      error,
    );
    return "0h 0m";
  }
};

export const isCurrentlyWorking = (status: EmployeeStatus): boolean => {
  return status.currentStatus === TimeEntryStatus.IN && !status.isOnBreak;
};

export const isCurrentlyOnBreak = (status: EmployeeStatus): boolean => {
  return (
    status.currentStatus === TimeEntryStatus.IN && status.isOnBreak === true
  );
};

export const canTimeIn = (status: EmployeeStatus): boolean => {
  return status.currentStatus !== TimeEntryStatus.IN;
};

export const canTimeOut = (status: EmployeeStatus): boolean => {
  // Can only time out if timed in and NOT on break
  return status.currentStatus === TimeEntryStatus.IN && !status.isOnBreak;
};

export const canStartBreak = (status: EmployeeStatus): boolean => {
  // Can only start break if timed in and NOT already on break
  return status.currentStatus === TimeEntryStatus.IN && !status.isOnBreak;
};

export const canEndBreak = (status: EmployeeStatus): boolean => {
  // Can only end break if currently on break
  return (
    status.currentStatus === TimeEntryStatus.IN && status.isOnBreak === true
  );
};

export const formatBreakDuration = (totalBreakMinutes?: number): string => {
  if (!totalBreakMinutes || totalBreakMinutes <= 0) {
    return "0m";
  }
  const hours = Math.floor(totalBreakMinutes / 60);
  const minutes = totalBreakMinutes % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// User permissions for time tracking
export interface TimeTrackingPermissions {
  canTimeInOut: boolean;
  canViewOwnTimesheet: boolean;
  canViewAllStatuses: boolean;
  canViewReports: boolean;
  canManageTimeEntries: boolean;
  canForceTimeOut: boolean;
  canExportData: boolean;
}

export const getTimeTrackingPermissions = (
  roles: string[],
): TimeTrackingPermissions => {
  const hasRole = (role: string) => roles.some((r) => r.includes(role));

  return {
    canTimeInOut: true, // All users can time in/out
    canViewOwnTimesheet: true, // All users can view their own timesheet
    canViewAllStatuses: true, // All users can view colleague statuses (per requirements)
    canViewReports: hasRole("ADMIN"), // Only admins can view comprehensive reports
    canManageTimeEntries: hasRole("ADMIN"), // Only admins can edit/delete entries
    canForceTimeOut: hasRole("ADMIN"), // Only admins can force time out
    canExportData: hasRole("ADMIN"), // Only admins can export data
  };
};

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Date range interface for filtering
export interface DateRange {
  startDate: string;
  endDate: string;
}

// Filter options for time entries
export interface TimeEntryFilter {
  employeeId?: number;
  dateRange?: DateRange;
  status?: TimeEntryStatus;
  searchTerm?: string;
}

// Pagination interface
export interface PaginationParams {
  page: number;
  size: number;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}
